import { Amplify } from "aws-amplify";
import { 
  signIn, 
  signUp, 
  signOut, 
  confirmSignUp, 
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  updateUserAttributes,
  confirmUserAttribute,
} from "aws-amplify/auth";
import i18next from "i18next";
import { getPhotohawkEnv } from "@/lib/env";
import {
  REFRESH_LEEWAY_MS,
  decodeJwtClaims,
  isJwtExpired,
  isJwtExpiringWithin,
} from "@/lib/tokenExpiry";

// Cognito User Pool IDs and *public* app client IDs are not secrets — they ship
// to every browser via Amplify, and photohawk-serverless hardcodes them per
// environment (src/layers/lib/src/auth.js + users.js). So we configure Amplify
// directly client-side (like photohawk-admin / photohawk-app do) keyed by the
// resolved environment, instead of fetching from an edge function. Region is
// eu-west-1 for both prod and staging (see photohawk-admin/.env.{prod,staging}).
const COGNITO_CONFIG = {
  prod: {
    userPoolId: "eu-west-1_MDqWcavk1",
    clientId: "3t97j44jlve43g99296n36th5p",
    region: "eu-west-1",
  },
  staging: {
    userPoolId: "eu-west-1_od7Hhgno7",
    clientId: "32lg1hs9eq8sdu4nhb1j6rgc01",
    region: "eu-west-1",
  },
} as const;

let isConfigured = false;
let cognitoClientId: string | null = null;

export const configureCognito = async () => {
  if (isConfigured) return;

  const config = COGNITO_CONFIG[getPhotohawkEnv()];
  cognitoClientId = config.clientId;

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.clientId,
      },
    },
  });

  isConfigured = true;
};

export const getCognitoClientId = () => cognitoClientId;

// The UI language goes to Cognito with every request that makes it send a code email, so the CustomMessage
// trigger (photohawk-serverless lib/cognitoMessages.js) writes that email in the same language.
export const codeEmailMetadata = (): Record<string, string> => {
  const lang = String(i18next.resolvedLanguage || i18next.language || "en").split("-")[0];
  return { lang };
};

export const cognitoSignIn = async (email: string, password: string) => {
  await configureCognito();
  return signIn({ username: email, password });
};

export const cognitoSignUp = async (email: string, password: string) => {
  await configureCognito();
  return signUp({
    username: email,
    password,
    options: {
      userAttributes: {
        email,
      },
      clientMetadata: codeEmailMetadata(),
    },
  });
};

export const cognitoConfirmSignUp = async (email: string, code: string) => {
  await configureCognito();
  return confirmSignUp({ username: email, confirmationCode: code });
};

export const cognitoResendCode = async (email: string) => {
  await configureCognito();
  return resendSignUpCode({ username: email, options: { clientMetadata: codeEmailMetadata() } });
};

export const cognitoForgotPassword = async (email: string) => {
  await configureCognito();
  return resetPassword({ username: email, options: { clientMetadata: codeEmailMetadata() } });
};

export const cognitoConfirmForgotPassword = async (email: string, code: string, newPassword: string) => {
  await configureCognito();
  return confirmResetPassword({ username: email, confirmationCode: code, newPassword });
};

export const cognitoCompleteNewPassword = async (newPassword: string) => {
  await configureCognito();
  return confirmSignIn({ challengeResponse: newPassword });
};

// Change password for an already-signed-in user (Cognito, via Amplify).
export const cognitoChangePassword = async (oldPassword: string, newPassword: string) => {
  await configureCognito();
  return updatePassword({ oldPassword, newPassword });
};

/**
 * Ask Cognito to change the signed-in user's email: it sends a verification code to `newEmail`
 * (UpdateUserAttributes). The pool keeps the CURRENT email active until the code is verified, so
 * nothing changes for sign-in yet. Calling it again re-sends a fresh code. Returns where the code went.
 */
export const cognitoRequestEmailChange = async (newEmail: string) => {
  await configureCognito();
  const out = await updateUserAttributes({ userAttributes: { email: newEmail }, options: { clientMetadata: codeEmailMetadata() } });
  return out.email?.nextStep?.codeDeliveryDetails?.destination ?? null;
};

/** Verify the emailed code (VerifyUserAttribute): the new address becomes the sign-in email. */
export const cognitoConfirmEmailChange = async (code: string) => {
  await configureCognito();
  return confirmUserAttribute({ userAttributeKey: "email", confirmationCode: code.trim() });
};

export const cognitoSignOut = async () => {
  await configureCognito();
  return signOut();
};

export const cognitoGetCurrentUser = async () => {
  await configureCognito();
  try {
    const user = await getCurrentUser();
    return user;
  } catch {
    return null;
  }
};

export const cognitoGetSession = async (forceRefresh = false) => {
  await configureCognito();
  try {
    const session = await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
    return session;
  } catch {
    return null;
  }
};

export interface CognitoTokens {
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
}

const NO_TOKENS: CognitoTokens = { idToken: null, accessToken: null, refreshToken: null };

/** The tokens Amplify currently holds (it refreshes them itself if already expired). */
export const getCognitoTokens = async (): Promise<CognitoTokens> => {
  await configureCognito();

  try {
    // Get tokens from Amplify session
    const session = await fetchAuthSession();
    const idToken = session?.tokens?.idToken?.toString() || null;
    const accessToken = session?.tokens?.accessToken?.toString() || null;

    // Get refresh token from localStorage (Amplify stores it there)
    const refreshToken = getRefreshTokenFromStorage();

    return {
      idToken,
      accessToken,
      refreshToken,
    };
  } catch {
    return { ...NO_TOKENS };
  }
};

export const getRefreshTokenFromStorage = (): string | null => {
  if (!cognitoClientId) return null;

  // Step 1: Get last authenticated username
  const lastUserKey = `CognitoIdentityServiceProvider.${cognitoClientId}.LastAuthUser`;
  const username = localStorage.getItem(lastUserKey);

  if (!username) return null;

  // Step 2: Build the refresh token key
  const refreshTokenKey = `CognitoIdentityServiceProvider.${cognitoClientId}.${username}.refreshToken`;

  // Step 3: Read the refresh token
  return localStorage.getItem(refreshTokenKey);
};

/**
 * Outcome of making sure the session has at least `minValidityMs` of life left:
 *  - `ok`: tokens are valid for at least that long (refreshed if they weren't).
 *  - `signedOut`: there is no session, or the refresh token itself was rejected
 *    (expired/revoked — Amplify clears storage in that case). Only THIS warrants the
 *    login screen.
 *  - `transient`: the refresh couldn't complete (offline, throttled, 5xx). The session
 *    may well still be good — retry later, don't sign the user out.
 */
export type SessionRefreshResult =
  | { status: "ok"; tokens: CognitoTokens }
  | { status: "signedOut" }
  | { status: "transient" };

/**
 * Cognito's clock minus ours (ms), as Amplify records it on every sign-in/refresh
 * (`iat` of the new access token vs local time). Add it to `Date.now()` to compare
 * against token expiries the way Amplify does — so a skewed device clock neither
 * hands off "fresh" tokens that are really expired nor refreshes in a loop.
 */
export const getClockDriftFromStorage = (): number => {
  if (!cognitoClientId || typeof localStorage === "undefined") return 0;
  try {
    const prefix = `CognitoIdentityServiceProvider.${cognitoClientId}`;
    const username = localStorage.getItem(`${prefix}.LastAuthUser`);
    if (!username) return 0;
    const drift = Number.parseInt(localStorage.getItem(`${prefix}.${username}.clockDrift`) ?? "0", 10);
    return Number.isFinite(drift) ? drift : 0;
  } catch {
    return 0;
  }
};

/** "Now" on Cognito's clock (see getClockDriftFromStorage). */
export const cognitoNow = (): number => Date.now() + getClockDriftFromStorage();

export const refreshCognitoSession = async (
  minValidityMs: number = REFRESH_LEEWAY_MS,
): Promise<SessionRefreshResult> => {
  await configureCognito();
  try {
    // A plain read already refreshes tokens Amplify considers expired (5s tolerance).
    let session = await fetchAuthSession();
    let idToken = session?.tokens?.idToken?.toString() || null;
    let accessToken = session?.tokens?.accessToken?.toString() || null;
    if (!idToken) return { status: "signedOut" };
    // …but not ones that expire in a minute. Anything leaving the page (a hand-off) or
    // kept alive by a timer must have real headroom, so force the refresh early.
    const now = cognitoNow();
    if (isJwtExpiringWithin(idToken, minValidityMs, now) || isJwtExpiringWithin(accessToken, minValidityMs, now)) {
      session = await fetchAuthSession({ forceRefresh: true });
      idToken = session?.tokens?.idToken?.toString() || null;
      accessToken = session?.tokens?.accessToken?.toString() || null;
      // Amplify returns no tokens (and clears them) when Cognito rejects the refresh
      // token as invalid/expired/revoked; transient failures throw instead.
      if (!idToken) return { status: "signedOut" };
    }
    // Read the refresh token AFTER any refresh: with rotation enabled Cognito issues a
    // new one and invalidates the old, so a value read earlier would be dead.
    return { status: "ok", tokens: { idToken, accessToken, refreshToken: getRefreshTokenFromStorage() } };
  } catch {
    return { status: "transient" };
  }
};

/**
 * Tokens with at least `minValidityMs` of life left (refreshing first if needed), for
 * handing off to another origin. All-null if the session can't be made fresh — callers
 * then send the user to the target's login without tokens rather than handing off a
 * token that is (about to be) expired.
 */
export const getFreshCognitoTokens = async (
  minValidityMs: number = REFRESH_LEEWAY_MS,
): Promise<CognitoTokens> => {
  const result = await refreshCognitoSession(minValidityMs);
  return result.status === "ok" ? result.tokens : { ...NO_TOKENS };
};

/**
 * `fetch` with the Cognito id token as `Authorization`. A 401 means the gateway
 * rejected the token (expired between our check and the call, clock skew, stale cache):
 * force a refresh from the refresh token and retry ONCE. A second 401 is returned to
 * the caller — the sign-in itself is gone.
 */
export const authFetch = async (input: string, init: RequestInit = {}): Promise<Response> => {
  const withToken = async (forceRefresh: boolean) => {
    const session = await cognitoGetSession(forceRefresh);
    const token = session?.tokens?.idToken?.toString();
    if (!token) throw new Error("You're not signed in.");
    const headers = new Headers(init.headers);
    headers.set("Authorization", token);
    return fetch(input, { ...init, headers });
  };
  const res = await withToken(false);
  if (res.status !== 401 || init.signal?.aborted) return res;
  return withToken(true);
};

/**
 * The `email` claim from an ID token. This is the reliable source of the
 * signed-in user's email — `getCurrentUser()`'s `signInDetails.loginId` is only
 * populated right after an Amplify `signIn()` in the same page load (lost on
 * reload / on a token-seeded session), and `username` can be the Cognito `sub`.
 */
export const emailFromIdToken = (idToken?: string | null): string | null => {
  const email = decodeJwtClaims(idToken)?.["email"];
  return typeof email === "string" ? email : null;
};

interface RefreshedTokens {
  idToken?: string;
  accessToken?: string;
  /** Present only when the app client rotates refresh tokens. */
  refreshToken?: string;
}

const callCognito = async (
  region: string,
  target: string,
  body: Record<string, unknown>,
): Promise<RefreshedTokens | null> => {
  try {
    const res = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const r = (await res.json())?.AuthenticationResult;
    if (!r?.IdToken || !r?.AccessToken) return null;
    return {
      idToken: r.IdToken as string,
      accessToken: r.AccessToken as string,
      refreshToken: (r.RefreshToken as string | undefined) || undefined,
    };
  } catch {
    return null;
  }
};

/**
 * Exchange a refresh token for a fresh id+access pair directly against Cognito's
 * public endpoint. We do this ourselves because Amplify's `fetchAuthSession({forceRefresh:true})`
 * can no-op on a COLD page load — with no in-memory session to refresh it doesn't
 * mint tokens, leaving storage incomplete (id+refresh but no access) so the app
 * never sees a session. This needs only the refresh token + client id.
 *
 * Uses GetTokensFromRefreshToken (what Amplify itself uses, and the only refresh API
 * that works when the client rotates refresh tokens — it then returns the new refresh
 * token, which the caller MUST store), falling back to InitiateAuth REFRESH_TOKEN_AUTH.
 */
export const refreshViaCognito = async (
  refreshToken: string,
  clientId: string,
  region = "eu-west-1",
): Promise<RefreshedTokens | null> =>
  (await callCognito(region, "GetTokensFromRefreshToken", { ClientId: clientId, RefreshToken: refreshToken })) ??
  (await callCognito(region, "InitiateAuth", {
    AuthFlow: "REFRESH_TOKEN_AUTH",
    ClientId: clientId,
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  }));

/**
 * Seed an Amplify Cognito session from raw tokens handed off via URL (the
 * workspace hand-off: `?token=<idToken>&accessToken=<at>&refreshToken=<rt>`). We write
 * the exact localStorage keys Amplify reads. Returns true on success.
 *
 * The handed-off id/access tokens may be expired or about to expire — the page that
 * built the link can have sat idle past their ~1h lifetime — or the access token may be
 * missing (Amplify won't hydrate a session without one). In all those cases a valid
 * refresh token is enough: we mint a fresh id+access pair from it FIRST, and only then
 * write storage. Only when the id token is expired AND the refresh token can't mint a
 * new one (itself expired/revoked) do we fail — and then we leave any session this host
 * already has untouched.
 */
export const seedCognitoSession = async (
  idToken: string,
  refreshToken?: string,
  accessToken?: string,
): Promise<boolean> => {
  await configureCognito();
  if (!cognitoClientId || typeof localStorage === "undefined") return false;

  const claims = decodeJwtClaims(idToken);
  const username =
    (claims?.["cognito:username"] as string | undefined) || (claims?.["sub"] as string | undefined);
  if (!username) return false;

  let id = idToken;
  let access = accessToken;
  let rt = refreshToken;
  const stale =
    !access || isJwtExpiringWithin(id, REFRESH_LEEWAY_MS) || isJwtExpiringWithin(access, REFRESH_LEEWAY_MS);
  if (rt && stale) {
    const region = COGNITO_CONFIG[getPhotohawkEnv()].region;
    const refreshed = await refreshViaCognito(rt, cognitoClientId, region);
    if (refreshed?.idToken && refreshed.accessToken) {
      id = refreshed.idToken;
      access = refreshed.accessToken;
      if (refreshed.refreshToken) rt = refreshed.refreshToken;
    }
  }

  // Nothing usable: expired id token and no fresh one could be minted.
  if (isJwtExpired(id)) return false;

  const prefix = `CognitoIdentityServiceProvider.${cognitoClientId}`;
  localStorage.setItem(`${prefix}.LastAuthUser`, username);
  localStorage.setItem(`${prefix}.${username}.idToken`, id);
  // Without a refresh token the seeded id (+ access) token still carries the session
  // for its lifetime; with one, Amplify keeps it alive from here on.
  if (rt) localStorage.setItem(`${prefix}.${username}.refreshToken`, rt);
  if (access) localStorage.setItem(`${prefix}.${username}.accessToken`, access);
  localStorage.setItem(`${prefix}.${username}.clockDrift`, "0");

  // Read the session back so Amplify adopts it. A failure here must NOT sink a hand-off
  // whose id token is valid — honour it.
  try {
    const session = await fetchAuthSession();
    if (session?.tokens?.idToken) return true;
  } catch {
    /* fall through */
  }
  return !isJwtExpired(id);
};
